/**
 * Creates Hypercube.js AABBs.
 * @example
 * const aabb = new AABB(vectormin, vectormax);
 */
class AABB {

    /**
     * Stores the position of the AABB.
     * @type {vector3}
     * @private
     */
    $translation;

    /**
     * Stores the maximum x value of the AABB.
     * @type {number}
     * @private
     */
    $xmax;

    /**
     * Stores the minimum x value of the AABB.
     * @type {number}
     * @private
     */
    $xmin;

    /**
     * Stores the maximum y value of the AABB.
     * @type {number}
     * @private
     */
    $ymax;

    /**
     * Stores the minimum y value of the AABB.
     * @type {number}
     * @private
     */
    $ymin;

    /**
     * Stores the maximum z value of the AABB.
     * @type {number}
     * @private
     */
    $zmax;

    /**
     * Stores the minimum z value of the AABB.
     * @type {number}
     * @private
     */
    $zmin;

    /**
     * Gets the position of the AABB.
     * @type {vector3}
     * @public
     * @readonly
     */
    get translation() {

        return this.$translation;
    }

    /**
     * Gets the maximum x value of the AABB.
     * @type {number}
     * @public
     * @readonly
     */
    get xmax() {

        return this.$xmax;
    }

    /**
     * Gets the minimum x value of the AABB.
     * @type {number}
     * @public
     * @readonly
     */
    get xmin() {

        return this.$xmin;
    }

    /**
     * Gets the maximum y value of the AABB.
     * @type {number}
     * @public
     * @readonly
     */
    get ymax() {

        return this.$ymax;
    }

    /**
     * Gets the minimum y value of the AABB.
     * @type {number}
     * @public
     * @readonly
     */
    get ymin() {

        return this.$ymin;
    }

    /**
     * Gets the maximum z value of the AABB.
     * @type {number}
     * @public
     * @readonly
     */
    get zmax() {

        return this.$zmax;
    }

    /**
     * Gets the minimum z value of the AABB.
     * @type {number}
     * @public
     * @readonly
     */
    get zmin() {

        return this.$zmin;
    }

    /**
     * Creates a new Hypercube.js AABB.
     * @param {vector3} $vectormin The minimum xyz values of the AABB to create.
     * @param {vector3} $vectormax The maximum xyz values of the AABB to create.
     */
    constructor($vectormin, $vectormax) {

        const [xmin, ymin, zmin] = $vectormin;
        const [xmax, ymax, zmax] = $vectormax;

        this.$xmin = xmin;
        this.$ymin = ymin;
        this.$zmin = zmin;

        this.$xmax = xmax;
        this.$ymax = ymax;
        this.$zmax = zmax;

        const xtranslation = (this.$xmin + this.$xmax) * 0.5;
        const ytranslation = (this.$ymin + this.$ymax) * 0.5;
        const ztranslation = (this.$zmin + this.$zmax) * 0.5;

        this.$translation = [xtranslation, ytranslation, ztranslation];
    }
}

export {

    AABB
};

export default AABB;
